import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Search, X, AlertTriangle, Clock } from 'lucide-react';
import { PortalHeader } from './PortalHeader';
import { PortalFooter } from './PortalFooter';
import { useBrand } from '@/contexts/BrandContext';
import { getErrorCode } from '@/services/api';
import { LoadingSpinner } from '@airclou/ui';

export function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { brand, isLoading, error } = useBrand();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  };

  // Show loading state while brand is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state if brand failed to load
  if (error || !brand) {
    const errorCode = error ? getErrorCode(error) : 'BRAND_NOT_FOUND';
    const isDisabled = errorCode === 'BRAND_DISABLED';

    return (
      <div className="min-h-screen flex flex-col bg-background">
        {/* Minimal header for error state */}
        <header className="h-16 border-b bg-background">
          <div className="mx-auto max-w-[1120px] h-full px-4 md:px-6 flex items-center">
            <span className="font-semibold text-lg text-foreground">Help Center</span>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${
                isDisabled
                  ? 'bg-amber-100 text-amber-600'
                  : 'bg-red-100 text-red-600'
              }`}
            >
              {isDisabled ? (
                <Clock className="h-8 w-8" />
              ) : (
                <AlertTriangle className="h-8 w-8" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {isDisabled
                ? 'Help Center Temporarily Unavailable'
                : 'Help Center Not Found'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {isDisabled
                ? 'This Help Center is currently disabled. Please check back later or contact support for assistance.'
                : 'The Help Center you are looking for could not be found. Please check the URL and try again.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors"
            >
              Try again
            </button>
          </div>
        </main>

        <PortalFooter />
      </div>
    );
  }

  // Hide header search on home page (has its own hero search) and search page (has its own search input)
  const isHomePage = location.pathname === '/';
  const isSearchPage = location.pathname === '/search';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PortalHeader
        onSearchClick={() => setMobileSearchOpen(true)}
        hideSearch={isHomePage || isSearchPage}
      />

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 bg-background md:hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMobileSearchOpen(false)}
                className="p-2 rounded-lg hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
              <span className="font-medium">Search</span>
            </div>
            <form onSubmit={handleMobileSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full h-12 pl-10 pr-4 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-lg"
                />
              </div>
            </form>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <PortalFooter />
    </div>
  );
}
